import Box from "@mui/material/Box";
import LeaderboardTableAbsolute from "components/Leaderboards/LeaderboardTableAbsolute";
import MessageWithLogo from "components/MessageWithLogo";
import MeasurementsFilterContext from "contexts/MeasurementsFilterContext";
import { useFormikContext } from "formik";
import useFilterMeasurementsByAthleteIds from "hooks/useFilterMeasurementsByAthleteIds";
import moment from "moment";
import { useMemo } from "react";
import { LeaderboardFormValues } from "../LeaderboardEditable.types";
import * as Components from "./LeaderboardEditableData.components";
import LeaderboardTablePercentage from "components/Leaderboards/LeaderboardTablePercentage";
import { DateTime, LeaderboardRankType } from "types";
import TickerNode from "components/Ticker/TickerNode";
import { useTranslation } from "react-i18next";
import LeaderboardHelpers from "features/Leaderboards/helpers/LeaderboardHelpers";

function LeaderboardEditableData() {
  const { t } = useTranslation();
  const { values } = useFormikContext<LeaderboardFormValues>();
  const { type, rankType, comparePeriodType, startDateComparePeriod } = values;

  // Get exercise, metric, and variant for Exercise type; circuits share the metric path (no variant)
  const exercise = values.measureType === "Exercise" ? values.exercise : null;
  const metric = values.measureType === "Exercise" || values.measureType === "Circuit" ? values.metric : null;
  const variant = values.measureType === "Exercise" ? values.variant : null;

  const filterMeasurements = useFilterMeasurementsByAthleteIds(values.groups);

  const period = useMemo(() => {
    const endDate = moment().endOf("day");

    if (type.value === "All" || comparePeriodType.value === "All") {
      return {
        startDate: moment("2019-01-01").toISOString(),
        endDate: endDate.toISOString(),
      };
    }

    if (rankType === LeaderboardRankType.PercentageOfBest) {
      if (comparePeriodType.value === "Custom") {
        return {
          startDate: moment(startDateComparePeriod).toISOString(),
          endDate: endDate.toISOString(),
        };
      }
      const comparePeriodKey = comparePeriodType.value.toLowerCase() as "week" | "month" | "year";

      return {
        startDate: endDate.clone().subtract(1, comparePeriodKey).toISOString(),
        endDate: endDate.toISOString(),
      };
    }

    const timePeriodKey = type.value.toLowerCase() as "day" | "week" | "month";

    return {
      startDate: endDate.clone().subtract(1, timePeriodKey).toISOString(),
      endDate: endDate.toISOString(),
    };
  }, [comparePeriodType.value, startDateComparePeriod, rankType, type.value]);

  const timePeriodStartDate: DateTime | undefined = useMemo(() => {
    if (rankType !== LeaderboardRankType.PercentageOfBest) {
      return;
    }

    if (type.value === "All") {
      throw new Error(t("leaderboard.relative_all_time_error"));
    }

    const timePeriodKey = type.value.toLowerCase() as "day" | "week" | "month";

    return moment().endOf("day").subtract(1, timePeriodKey).toISOString();
  }, [rankType, type.value, t]);

  // Create leaderboard object for LeaderboardTableAbsolute
  const leaderboard = useMemo(() => {
    try {
      const leaderboardBase = LeaderboardHelpers.fromForm(values);
      // Add id field to convert LeaderboardBase to Leaderboard
      return {
        ...leaderboardBase,
        id: values.id || "",
      };
    } catch {
      return null;
    }
  }, [values]);

  // Check if leaderboard is complete
  const isComplete =
    values.measureType === "Score"
      ? Boolean(values.scoreMetadata)
      : values.measureType === "Circuit"
        ? Boolean(values.circuit && metric)
        : Boolean(exercise && metric);

  return (
    <MeasurementsFilterContext.Provider value={filterMeasurements}>
      <Box
        sx={{
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          position: "relative",
        }}
      >
        <Components.OverflowBox flex={1}>
          {isComplete && leaderboard ? (
            <Box>
              {rankType !== LeaderboardRankType.PercentageOfBest ? (
                <LeaderboardTableAbsolute
                  leaderboard={leaderboard}
                  exercise={exercise || undefined}
                  metric={metric || undefined}
                  variant={variant}
                  {...period}
                />
              ) : (
                <LeaderboardTablePercentage
                  leaderboard={leaderboard}
                  exercise={exercise || undefined}
                  metric={metric || undefined}
                  variant={variant || null}
                  {...period}
                  comparePeriodType={comparePeriodType}
                  timePeriodStartDate={timePeriodStartDate}
                  startDateComparePeriod={startDateComparePeriod}
                />
              )}
            </Box>
          ) : (
            <Box
              sx={{
                px: 2,
              }}
            >
              <MessageWithLogo>
                {t(
                  values.measureType === "Score"
                    ? "leaderboard.no_score_selected_error"
                    : "leaderboard.no_exercise_selected_error"
                )}
              </MessageWithLogo>
            </Box>
          )}
        </Components.OverflowBox>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <TickerNode />
        </Box>
      </Box>
    </MeasurementsFilterContext.Provider>
  );
}

export default LeaderboardEditableData;
